import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { fmtDateTime } from "@/lib/format";
import { Send, Trash2, Square, CheckSquare, X, Loader2 } from "lucide-react";
import ConfirmDialog from "@/components/ConfirmDialog";

const typeMeta = {
  prezzi: { color: "text-blue-600 bg-blue-50", label: "Prezzi" },
  report: { color: "text-purple-600 bg-purple-50", label: "Report" },
  news: { color: "text-amber-600 bg-amber-50", label: "News" },
  sistema: { color: "text-slate-600 bg-slate-100", label: "Sistema" },
};

export default function AdminNotifications() {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({ title: "", message: "", type: "sistema", target_type: "all", target_client_ids: [] });
  const [selected, setSelected] = useState(new Set());
  const [busy, setBusy] = useState(false);
  const [confirmState, setConfirmState] = useState(null);

  const load = async () => { const { data } = await api.get("/notifications"); setItems(data); };
  useEffect(() => { load(); }, []);

  const send = async () => {
    if (!form.title || !form.message) return toast.error("Compila titolo e messaggio");
    await api.post("/notifications", form);
    toast.success("Notifica inviata");
    setForm({ title: "", message: "", type: "sistema", target_type: "all", target_client_ids: [] });
    load();
  };

  const toggleSelect = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };
  const toggleSelectAll = () => {
    const ids = items.map((n) => n.id);
    const allSelected = ids.length > 0 && ids.every((id) => selected.has(id));
    setSelected(allSelected ? new Set() : new Set(ids));
  };
  const clearSelection = () => setSelected(new Set());

  const deleteOne = (n) => {
    setConfirmState({
      title: "Eliminare la notifica?",
      message: `"${n.title}" verrà rimossa per tutti gli utenti (anche in area clienti).`,
      confirmLabel: "Elimina",
      variant: "danger",
      onConfirm: async () => {
        try {
          await api.delete(`/notifications/${n.id}`);
          toast.success("Notifica eliminata");
          window.dispatchEvent(new CustomEvent("notifications:changed"));
          await load();
        } catch (e) {
          toast.error(e.response?.data?.detail || "Errore eliminazione");
        }
      },
    });
  };

  const bulkDelete = () => {
    if (selected.size === 0) return;
    setConfirmState({
      title: `Eliminare ${selected.size} notific${selected.size === 1 ? "a" : "he"}?`,
      message: "Le notifiche selezionate verranno rimosse per tutti gli utenti — anche i clienti non le vedranno più.",
      confirmLabel: "Elimina tutte",
      variant: "danger",
      onConfirm: async () => {
        setBusy(true);
        try {
          const { data } = await api.post("/notifications/bulk-delete", { ids: Array.from(selected) });
          toast.success(`${data.deleted} notifiche eliminate`);
          clearSelection();
          window.dispatchEvent(new CustomEvent("notifications:changed"));
          await load();
        } catch (e) {
          toast.error(e.response?.data?.detail || "Errore eliminazione");
        } finally {
          setBusy(false);
        }
      },
    });
  };

  const allSelected = items.length > 0 && items.every((n) => selected.has(n.id));

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      <ConfirmDialog state={confirmState} onClose={() => setConfirmState(null)} />
      <div>
        <h1 className="font-display text-3xl font-bold tracking-tight text-slate-900 mb-4">Nuova notifica</h1>
        <div className="card-elevated p-5 space-y-3">
          <input placeholder="Titolo" value={form.title} onChange={(e) => setForm({...form, title: e.target.value})} className="w-full px-4 py-2.5 rounded-lg border border-slate-200 text-sm outline-none focus:border-blue-500" data-testid="notif-title" />
          <textarea placeholder="Messaggio" value={form.message} onChange={(e) => setForm({...form, message: e.target.value})} rows={4} className="w-full px-4 py-2.5 rounded-lg border border-slate-200 text-sm outline-none focus:border-blue-500" data-testid="notif-message" />
          <select value={form.type} onChange={(e) => setForm({...form, type: e.target.value})} className="w-full px-4 py-2.5 rounded-lg border border-slate-200 text-sm bg-white outline-none focus:border-blue-500">
            <option value="sistema">Sistema</option>
            <option value="prezzi">Prezzi</option>
            <option value="report">Report</option>
            <option value="news">News</option>
          </select>
          <button onClick={send} className="btn-brand flex items-center gap-2" data-testid="notif-send"><Send size={16} /> Invia a tutti i clienti</button>
        </div>
      </div>

      <div>
        <div className="flex items-baseline justify-between mb-4 flex-wrap gap-2">
          <h2 className="font-display text-3xl font-bold tracking-tight text-slate-900">Storico</h2>
          <span className="text-xs text-slate-500">{items.length} totali</span>
        </div>

        {/* Bulk toolbar */}
        {items.length > 0 && (
          <div className="flex items-center gap-3 flex-wrap text-sm mb-3">
            <button
              onClick={toggleSelectAll}
              className="flex items-center gap-1.5 text-slate-600 hover:text-blue-600 font-medium"
              data-testid="admin-notif-select-all"
            >
              {allSelected ? <CheckSquare size={16} className="text-blue-600" /> : <Square size={16} />}
              {allSelected ? "Deseleziona tutto" : "Seleziona tutto"}
            </button>
            {selected.size > 0 && (
              <>
                <span className="text-slate-400">·</span>
                <span className="font-semibold text-blue-900">{selected.size} selezionat{selected.size === 1 ? "a" : "e"}</span>
                <button
                  onClick={bulkDelete}
                  disabled={busy}
                  className="ml-1 bg-red-600 hover:bg-red-700 text-white rounded-lg px-3 py-1.5 font-semibold flex items-center gap-1.5 disabled:opacity-60"
                  data-testid="admin-notif-bulk-delete"
                >
                  {busy ? <Loader2 size={13} className="animate-spin" /> : <Trash2 size={13} />}
                  Elimina selezionate
                </button>
                <button
                  onClick={clearSelection}
                  className="text-slate-500 hover:text-slate-900 flex items-center gap-1"
                  data-testid="admin-notif-bulk-clear"
                >
                  <X size={13} /> Annulla
                </button>
              </>
            )}
          </div>
        )}

        <div className="space-y-2 max-h-[600px] overflow-auto pr-1">
          {items.length === 0 && <div className="card-elevated p-8 text-center text-slate-500 text-sm">Nessuna notifica</div>}
          {items.map((n) => {
            const meta = typeMeta[n.type] || typeMeta.sistema;
            const isSelected = selected.has(n.id);
            return (
              <div
                key={n.id}
                className={`card-elevated p-4 flex items-start gap-3 group ${isSelected ? "ring-2 ring-blue-300 bg-blue-50/60" : ""}`}
                data-testid={`admin-notif-${n.id}`}
              >
                <button
                  onClick={() => toggleSelect(n.id)}
                  className="flex-shrink-0 text-slate-400 hover:text-blue-600 transition-colors mt-0.5"
                  aria-label={isSelected ? "Deseleziona" : "Seleziona"}
                  data-testid={`admin-notif-select-${n.id}`}
                >
                  {isSelected ? <CheckSquare size={16} className="text-blue-600" /> : <Square size={16} />}
                </button>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <div className="font-semibold text-slate-900 text-sm line-clamp-1">{n.title}</div>
                    <span className={`text-[10px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded ${meta.color}`}>{meta.label}</span>
                    {n.target_type !== "all" && (
                      <span className="text-[10px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded bg-purple-100 text-purple-700" title="Notifica destinata a clienti specifici">
                        Targeted
                      </span>
                    )}
                  </div>
                  <div className="text-xs text-slate-500 mt-1 line-clamp-2">{n.message}</div>
                  <div className="text-[10px] text-slate-400 mt-1">{fmtDateTime(n.sent_at)}</div>
                </div>
                <button
                  onClick={() => deleteOne(n)}
                  className="text-slate-400 hover:text-red-600 transition-colors opacity-0 group-hover:opacity-100 flex-shrink-0"
                  aria-label="Elimina"
                  title="Elimina notifica"
                  data-testid={`admin-notif-delete-${n.id}`}
                >
                  <Trash2 size={14} />
                </button>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
